import { Box, Text } from '@chakra-ui/react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { useCallback } from 'react';
import { GridScrollSeekPlaceholderProps, VirtuosoGrid } from 'react-virtuoso';
import { IClientApiResponse } from '~/clientApi/clientApi.types';
import { Creator } from '~/clientApi/creator.model';
import { TalentRosterEmptyResults } from '~/components/TalentRosterEmptyResults';
import { useTalentsGridState } from '~/screens/WhalarModeGlobalSearchScreen/store/useTalentScrollPosition';
import { useListsStore } from '~/screens/WhalarModeListDetailScreen/store/useSavedListStore';
import { StackViewCreator } from '~/screens/WhalarModeScreen/components/StackView/StackViewCreator';
import {
  useAdvancedFiltersStore,
  useMarketerSearchTalentStore,
  useTalentFiltersStore,
} from '~/screens/store';
import { SearchCreatorsApiResponse } from '~/searchApi';
import { ICreator } from '~/types';

import { InviteCreatorEmptyPage } from './InviteCreatorEmptyPage';
import { StackCreatorSkeletons } from './StackCreatorSkeletons';

interface StackViewProps {
  marketerData: ICreator[];
  error: unknown;
  hasNextPage?: boolean;
  isInitialFetch: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isShared: boolean;
  fetchNextPage?: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      IClientApiResponse<Creator> | SearchCreatorsApiResponse | undefined,
      unknown
    >
  >;
}

const ScrollSeekPlaceholder: React.FC<GridScrollSeekPlaceholderProps> = () => {
  return <StackCreatorSkeletons />;
};

export const StackView: React.FC<StackViewProps> = ({
  marketerData,
  error,
  hasNextPage,
  isInitialFetch,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isShared,
}) => {
  const { appliedFilters: advancedFilters } = useAdvancedFiltersStore();
  const { appliedFilters: creatorsFilters } = useTalentFiltersStore();
  const appliedFilters = [...advancedFilters, ...creatorsFilters];
  const { creators: storedCreators } = useListsStore();
  const { query } = useMarketerSearchTalentStore();
  const { state, setState } = useTalentsGridState();

  const handleEndReached = useCallback(() => {
    if (hasNextPage === true && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const itemContent = useCallback(
    (index: number) => {
      const creator = marketerData[index];
      const isChecked = !!storedCreators.find((c) => c.id === creator.id);

      return (
        <StackViewCreator
          creator={creator}
          isShared={isShared}
          isCheckEnabled={true}
          isChecked={isChecked}
        />
      );
    },
    [marketerData, storedCreators, isShared],
  );

  if (isInitialFetch) {
    return <StackCreatorSkeletons />;
  }

  if (error) {
    return <Text color="error.500">Something went wrong.</Text>;
  }

  if (isLoading) {
    return <StackCreatorSkeletons />;
  }

  if (
    (appliedFilters.length && !marketerData.length) ||
    (!marketerData.length && query.length)
  ) {
    return <TalentRosterEmptyResults />;
  }

  if (!marketerData.length) {
    return <InviteCreatorEmptyPage />;
  }

  return (
    <Box marginBottom={20}>
      <VirtuosoGrid
        restoreStateFrom={state}
        stateChanged={setState}
        useWindowScroll
        endReached={handleEndReached}
        totalCount={marketerData.length}
        itemContent={itemContent}
        overscan={5}
        increaseViewportBy={200}
        components={{
          ScrollSeekPlaceholder,
        }}
      />
    </Box>
  );
};
